import { type CSSProperties } from "react";
import { useWrappedApiWizard, type WizardSeed } from "../../hooks/use-wrapped-api-wizard";
import { Step1Spec } from "./wizard/Step1Spec";
import { Step2Identify } from "./wizard/Step2Identify";
import { Step3Operations } from "./wizard/Step3Operations";
import { Step4Mapping } from "./wizard/Step4Mapping";
import { Step5TestDone } from "./wizard/Step5TestDone";

/**
 * Sprint 16d — `<ConnectApiWizard>`. The "wow" layer over the manual 16a–c flow:
 * paste a spec, accept-or-edit AI proposals across five steps, land a fully
 * configured + tested wrapper in ~3 minutes. Each step pre-fills AI proposals as
 * editable inputs (Q6). "Configure manually" at any step drops into the existing
 * chrome (S16D.15).
 */

const STEP_LABELS = ["Spec", "Identify", "Operations", "Mapping", "Test"];

export function ConnectApiWizard({
  open,
  onOpenChange,
  onDone,
  onConfigureManually,
  seed,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the wizard completes (or the owner jumps to the detail page). */
  onDone: (slug: string) => void;
  /** Drop into the manual AddWrappedApiModal with whatever's been entered (S16D.15). */
  onConfigureManually: (seed: { name: string; slug: string; url: string }) => void;
  seed?: WizardSeed;
}) {
  const wizard = useWrappedApiWizard(seed);
  if (!open) return null;

  function close() {
    if (!wizard.analyzing && !wizard.persisting) onOpenChange(false);
  }

  function configureManually() {
    // Past Step 2 the wrapper exists — send the owner to its detail page (full
    // manual chrome). Otherwise hand the entered name/slug/url to the manual modal.
    if (wizard.created) {
      onOpenChange(false);
      onDone(wizard.slug);
    } else {
      onOpenChange(false);
      onConfigureManually({ name: wizard.name, slug: wizard.slug, url: wizard.specUrl });
    }
  }

  async function onPrimary() {
    if (wizard.step === 1) await wizard.analyze();
    else if (wizard.step === 2) await wizard.persistIdentify();
    else if (wizard.step === 3) await wizard.persistOperations();
    else if (wizard.step === 4) wizard.goTo(5);
    else onDone(wizard.slug);
  }

  const primaryLabel = (() => {
    if (wizard.step === 1) return wizard.analyzing ? "Analysing…" : "Analyse spec";
    if (wizard.step === 2) return wizard.persisting ? "Creating…" : "Continue";
    if (wizard.step === 3) return wizard.persisting ? "Saving…" : "Continue";
    if (wizard.step === 4) return "Continue";
    return "Finish";
  })();

  return (
    <div style={overlay} onMouseDown={(e) => e.target === e.currentTarget && close()}>
      <div role="dialog" aria-modal="true" aria-label="Connect a new API" style={box}>
        <div style={head}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <i className="ti ti-sparkles" style={{ fontSize: 16, color: "var(--color-text-info)" }} aria-hidden="true" />
            <h2 style={{ fontSize: 16, fontWeight: 500, margin: 0 }}>Connect a new API</h2>
          </div>
          <button type="button" onClick={close} style={closeBtn} aria-label="Close">
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden="true" />
          </button>
        </div>

        <StepProgress current={wizard.step} />

        <div style={scrollBody}>
          {wizard.step === 1 && <Step1Spec wizard={wizard} />}
          {wizard.step === 2 && <Step2Identify wizard={wizard} />}
          {wizard.step === 3 && <Step3Operations wizard={wizard} />}
          {wizard.step === 4 && <Step4Mapping wizard={wizard} />}
          {wizard.step === 5 && <Step5TestDone wizard={wizard} />}

          {wizard.error && <div style={errorStyle}>{wizard.error}</div>}
        </div>

        <div style={footer}>
          <button
            type="button"
            onClick={wizard.back}
            disabled={wizard.step === 1 || wizard.analyzing || wizard.persisting}
            style={{ ...ghostBtn, visibility: wizard.step === 1 ? "hidden" : "visible" }}
          >
            Back
          </button>
          <div style={{ flex: 1 }} />
          <button type="button" onClick={configureManually} style={ghostBtn}>
            Configure manually
          </button>
          <button
            type="button"
            onClick={onPrimary}
            disabled={wizard.analyzing || wizard.persisting}
            style={{ ...primaryBtn, opacity: wizard.analyzing || wizard.persisting ? 0.6 : 1 }}
          >
            {primaryLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

function StepProgress({ current }: { current: number }) {
  return (
    <div style={progressRow}>
      {STEP_LABELS.map((label, i) => {
        const n = i + 1;
        const state = n < current ? "done" : n === current ? "active" : "todo";
        return (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, flex: i < STEP_LABELS.length - 1 ? 1 : 0 }}>
            <div style={dot(state)}>{state === "done" ? <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden="true" /> : n}</div>
            <span style={{ fontSize: 12, color: state === "todo" ? "var(--color-text-tertiary)" : "var(--color-text-primary)", fontWeight: state === "active" ? 500 : 400, whiteSpace: "nowrap" }}>
              {label}
            </span>
            {i < STEP_LABELS.length - 1 && <div style={{ flex: 1, height: 1, background: "var(--color-border-secondary)" }} />}
          </div>
        );
      })}
    </div>
  );
}

const overlay: CSSProperties = {
  position: "fixed",
  inset: 0,
  background: "rgba(0,0,0,0.35)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 200,
  fontFamily: "var(--font-sans)",
  padding: 24,
};
const box: CSSProperties = {
  background: "var(--color-background-primary)",
  border: "0.5px solid var(--color-border-secondary)",
  borderRadius: "var(--border-radius-lg)",
  maxWidth: 720,
  width: "100%",
  maxHeight: "90vh",
  display: "flex",
  flexDirection: "column",
};
const head: CSSProperties = {
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  padding: "16px 20px 12px",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};
const progressRow: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 20px",
  borderBottom: "0.5px solid var(--color-border-secondary)",
};
const scrollBody: CSSProperties = { padding: "16px 20px", overflowY: "auto", flex: 1 };
const footer: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  padding: "12px 20px",
  borderTop: "0.5px solid var(--color-border-secondary)",
};
const closeBtn: CSSProperties = {
  border: "none",
  background: "none",
  cursor: "pointer",
  color: "var(--color-text-tertiary)",
  padding: 4,
  display: "inline-flex",
};
const errorStyle: CSSProperties = {
  fontSize: 12,
  color: "var(--c-red-600, var(--color-text-primary))",
  background: "var(--c-red-50, var(--color-background-secondary))",
  border: "var(--kuvio-border-default)",
  borderRadius: "var(--border-radius-sm)",
  padding: "8px 10px",
  marginTop: 8,
};
const ghostBtn: CSSProperties = {
  fontSize: 13,
  padding: "7px 14px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "transparent",
  color: "var(--color-text-secondary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};
const primaryBtn: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  padding: "7px 16px",
  borderRadius: "var(--border-radius-sm)",
  border: "none",
  background: "var(--color-text-primary)",
  color: "var(--color-background-primary)",
  cursor: "pointer",
  fontFamily: "var(--font-sans)",
};

function dot(state: "done" | "active" | "todo"): CSSProperties {
  return {
    width: 22,
    height: 22,
    flexShrink: 0,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 11,
    fontWeight: 500,
    border: state === "todo" ? "var(--kuvio-border-default)" : "none",
    background:
      state === "active"
        ? "var(--color-text-info)"
        : state === "done"
          ? "var(--c-green-600, var(--color-text-info))"
          : "var(--color-background-secondary)",
    color: state === "todo" ? "var(--color-text-tertiary)" : "var(--color-background-primary)",
  };
}
